import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles } from "../shared/styles";
import { TabBar } from "../shared/TabBar";
import { BackButton } from "../shared/BackButton";
import { AssetList } from "../inspections/AssetList";
import { SiteFormModal } from "./SiteFormModal";
import { SiteContactModal } from "./SiteContactModal";
import { SiteScheduleModal } from "./SiteScheduleModal";
import { NewEFSMRow } from "./NewEFSMRow";
import { ProceduresTab } from "./ProceduresTab";
import { AssetRegisterTabs } from "./AssetRegisterTabs";

export function SiteDetail({ siteId, accountId, onBack }) {
  const [site, setSite] = useState(null);
  const [efsms, setEfsms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("general");
  const [showEditSite, setShowEditSite] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAddEFSM, setShowAddEFSM] = useState(false);
  const [selectedEFSM, setSelectedEFSM] = useState(null);
  const loadSite = () => {
    return Promise.all([
      axios.get(`${API}/sites/${siteId}/`),
      axios.get(`${API}/site-efsms/`),
    ])
      .then(([siteRes, efsmsRes]) => {
        setSite(siteRes.data);
        setEfsms(efsmsRes.data.filter((e) => e.site === siteId && !e.unit && !e.area));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };
  useEffect(() => {
    loadSite();
  }, [siteId]);
  if (selectedEFSM)
    return (
      <AssetList
        efsmId={selectedEFSM.id}
        categoryName={selectedEFSM.categoryName}
        categoryId={selectedEFSM.categoryId}
        onBack={() => setSelectedEFSM(null)}
      />
    );
  if (loading) return <div style={styles.loading}>Loading...</div>;
  if (!site) return <div style={styles.loading}>Site not found.</div>;
  const tabs = ["general", "baseline", "contacts", "efsms", "asset_register", "procedures"];
  const tabLabels = {
    general: "General",
    baseline: "Baseline Data",
    contacts: "Site Contacts",
    efsms: "Site EFSMs",
    asset_register: "Asset Register",
    procedures: "Procedures",
  };
  return (
    <div>
      <div style={styles.topbar}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <BackButton onClick={onBack} />
          <div style={styles.pageTitle}>{site.site_address}</div>
        </div>
      </div>
      <TabBar
        tabs={tabs}
        tabLabels={tabLabels}
        activeTab={activeTab}
        onSelect={setActiveTab}
      />
      <div style={styles.content}>
        {activeTab === "general" && (
          <div style={styles.card}>
            <div
              style={{
                ...styles.tableHeader,
                gridTemplateColumns: "2fr 2fr 1fr 80px",
              }}
            >
              <div>Site Name</div>
              <div>Address</div>
              <div>Status</div>
              <div></div>
            </div>
            <div
              style={{
                ...styles.tableRow(false),
                gridTemplateColumns: "2fr 2fr 1fr 80px",
              }}
            >
              <div style={{ fontWeight: "500" }}>{site.site_name}</div>
              <div style={{ fontSize: "13px", color: "#666" }}>
                {site.site_address}
              </div>
              <div>
                <span style={styles.statusBadge(site.status)}>
                  {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
                </span>
              </div>
              <div>
                <button
                  onClick={() => setShowEditSite(true)}
                  style={{
                    backgroundColor: colors.blue,
                    color: colors.white,
                    border: "none",
                    padding: "4px 14px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
            {site.notes && (
              <div
                style={{
                  padding: "16px 20px",
                  borderTop: `1px solid ${colors.border}`,
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#999",
                    textTransform: "uppercase",
                    marginBottom: "4px",
                  }}
                >
                  Notes
                </div>
                <div style={{ fontSize: "14px" }}>{site.notes}</div>
              </div>
            )}
            {site.annual_value > 0 && (
              <div
                style={{
                  padding: "16px 20px",
                  borderTop: `1px solid ${colors.border}`,
                  display: "flex",
                  gap: "32px",
                  flexWrap: "wrap",
                }}
              >
                {site.monthly_fee && (
                  <div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#999",
                        textTransform: "uppercase",
                      }}
                    >
                      Monthly Fee
                    </div>
                    <div style={{ fontWeight: "600" }}>
                      ${parseFloat(site.monthly_fee).toFixed(2)} ×{" "}
                      {site.monthly_fee_months}mo
                    </div>
                  </div>
                )}
                {site.bi_annual_fee && (
                  <div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#999",
                        textTransform: "uppercase",
                      }}
                    >
                      Bi-Annual Fee
                    </div>
                    <div style={{ fontWeight: "600" }}>
                      ${parseFloat(site.bi_annual_fee).toFixed(2)}
                    </div>
                  </div>
                )}
                {site.annual_fee && (
                  <div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#999",
                        textTransform: "uppercase",
                      }}
                    >
                      Annual Fee
                    </div>
                    <div style={{ fontWeight: "600" }}>
                      ${parseFloat(site.annual_fee).toFixed(2)}
                    </div>
                  </div>
                )}
                <div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#999",
                      textTransform: "uppercase",
                    }}
                  >
                    Annual Contract Value
                  </div>
                  <div
                    style={{
                      fontWeight: "700",
                      color: colors.green,
                      fontSize: "16px",
                    }}
                  >
                    ${parseFloat(site.annual_value).toFixed(2)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === "baseline" && (
          <div style={styles.card}>
            <div style={{ ...styles.tableHeader, gridTemplateColumns: "1fr" }}>
              <div>Baseline Data</div>
            </div>
            <div
              style={{
                padding: "24px",
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "24px",
              }}
            >
              {[
                ["Levels Above Ground", site.levels_above_ground],
                ["Basement Carpark Levels", site.basement_carpark_levels],
                ["Outside Parking", site.has_outside_parking ? "Yes" : "No"],
                ["Year of Construction", site.year_of_construction],
              ].map(([label, val]) => (
                <div key={label}>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#999",
                      textTransform: "uppercase",
                      marginBottom: "4px",
                    }}
                  >
                    {label}
                  </div>
                  <div style={{ fontSize: "15px", fontWeight: "500" }}>
                    {val ?? "—"}
                  </div>
                </div>
              ))}
              <div style={{ gridColumn: "1 / -1" }}>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#999",
                    textTransform: "uppercase",
                    marginBottom: "8px",
                  }}
                >
                  Building Classes
                </div>
                {site.building_classes && site.building_classes.length > 0 ? (
                  <div
                    style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                  >
                    {site.building_classes.map((bc) => (
                      <span
                        key={bc.id}
                        style={{
                          backgroundColor: colors.lightGrey,
                          border: `1px solid ${colors.border}`,
                          borderRadius: "6px",
                          padding: "4px 12px",
                          fontSize: "13px",
                          fontWeight: "500",
                        }}
                      >
                        {bc.code}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: "14px", color: "#999" }}>
                    No building classes assigned
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {activeTab === "contacts" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "12px",
              }}
            >
              <button
                style={styles.addButton}
                onClick={() => setShowAddContact(true)}
              >
                + Add Site Contact
              </button>
            </div>
            <div style={styles.card}>
              {!site.contacts || site.contacts.length === 0 ? (
                <div style={styles.loading}>No contacts on file.</div>
              ) : (
                <>
                  <div
                    style={{
                      ...styles.tableHeader,
                      gridTemplateColumns: "2fr 1.5fr 1.5fr 2fr 1fr",
                    }}
                  >
                    <div>Name</div>
                    <div>Role</div>
                    <div>Phone / Mobile</div>
                    <div>Email</div>
                    <div>Reports</div>
                  </div>
                  {site.contacts.map((contact) => (
                    <div
                      key={contact.id}
                      style={{
                        ...styles.tableRow(false),
                        gridTemplateColumns: "2fr 1.5fr 1.5fr 2fr 1fr",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: "500" }}>{contact.name}</div>
                        {contact.position && (
                          <div style={{ fontSize: "12px", color: "#999" }}>
                            {contact.position}
                          </div>
                        )}
                      </div>
                      <div>
                        {contact.role.charAt(0).toUpperCase() +
                          contact.role.slice(1)}
                      </div>
                      <div>
                        {contact.phone && <div>{contact.phone}</div>}
                        {contact.mobile && (
                          <div style={{ fontSize: "12px", color: "#999" }}>
                            {contact.mobile}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: "13px" }}>{contact.email}</div>
                      <div>
                        {contact.receives_reports ? (
                          <span style={{ color: "#27ae60", fontWeight: "600" }}>
                            Yes
                          </span>
                        ) : (
                          <span style={{ color: "#999" }}>No</span>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}
        {activeTab === "efsms" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "12px",
                gap: "12px",
              }}
            >
              <button
                style={styles.blueButton}
                onClick={() => setShowAddEFSM(true)}
              >
                ⚙ Site Schedule
              </button>
            </div>
            <div style={styles.card}>
              {efsms.length === 0 ? (
                <div style={styles.loading}>
                  No EFSMs configured. Add one below.
                </div>
              ) : (
                <>
                  <div
                    style={{
                      ...styles.tableHeader,
                      gridTemplateColumns: "2fr 1fr 1fr 1fr 36px",
                    }}
                  >
                    <div>Category</div>
                    <div>Monthly</div>
                    <div>Bi-Annual</div>
                    <div>Annual</div>
                    <div></div>
                  </div>
                  {efsms.map((efsm) => (
                    <div
                      key={efsm.id}
                      style={{
                        ...styles.tableRow(false),
                        gridTemplateColumns: "2fr 1fr 1fr 1fr 36px",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "500",
                          color: colors.text,
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          setSelectedEFSM({
                            id: efsm.id,
                            categoryName: efsm.category_name,
                            categoryId: efsm.category,
                          })
                        }
                      >
                        {efsm.category_name}
                      </div>
                      {/* Monthly checkbox */}
                      <div>
                        <input
                          type="checkbox"
                          checked={efsm.is_monthly}
                          onChange={async (e) => {
                            await axios.patch(`${API}/site-efsms/${efsm.id}/`, {
                              is_monthly: e.target.checked,
                            });
                            loadSite();
                          }}
                        />
                      </div>
                      {/* Bi-Annual checkbox */}
                      <div>
                        <input
                          type="checkbox"
                          checked={efsm.is_bi_annual}
                          onChange={async (e) => {
                            await axios.patch(`${API}/site-efsms/${efsm.id}/`, {
                              is_bi_annual: e.target.checked,
                            });
                            loadSite();
                          }}
                        />
                      </div>
                      {/* Annual checkbox */}
                      <div>
                        <input
                          type="checkbox"
                          checked={efsm.is_annual}
                          onChange={async (e) => {
                            await axios.patch(`${API}/site-efsms/${efsm.id}/`, {
                              is_annual: e.target.checked,
                            });
                            loadSite();
                          }}
                        />
                      </div>
                      {/* Delete */}
                      <button
                        onClick={() => {
                          if (window.confirm("Remove this EFSM?")) {
                            axios
                              .delete(`${API}/site-efsms/${efsm.id}/`)
                              .then(() => loadSite());
                          }
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#ccc",
                          fontSize: "16px",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = colors.red)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = "#ccc")
                        }
                      >
                        🗑
                      </button>
                    </div>
                  ))}
                </>
              )}

              {/* Inline new EFSM row */}
              <NewEFSMRow siteId={siteId} onSave={() => loadSite()} />

              <div style={styles.count}>
                {efsms.length} EFSM{efsms.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
        )}
        {activeTab === "asset_register" && (
          <AssetRegisterTabs siteId={siteId} site={site} onSiteChange={loadSite} />
        )}

        {activeTab === "procedures" && <ProceduresTab siteId={siteId} />}
      </div>
      {showEditSite && (
        <SiteFormModal
          accountId={accountId}
          existingSite={site}
          onClose={() => setShowEditSite(false)}
          onSave={() => {
            setLoading(true);
            loadSite();
          }}
        />
      )}
      {showAddContact && (
        <SiteContactModal
          siteId={siteId}
          onClose={() => setShowAddContact(false)}
          onSave={() => loadSite()}
        />
      )}
      {showAddEFSM && (
        <SiteScheduleModal
          siteId={siteId}
          efsms={efsms}
          onClose={() => setShowAddEFSM(false)}
          onSave={() => loadSite()}
        />
      )}
    </div>
  );
}
